<template>
  <div class="py-4">
    <p class="font-medium text-lg">
      <span v-if="count == 0">Invite a member</span>
      <span v-else>Invite another member</span>
    </p>

    <!-- Ids are prefixed so they stay unique next to other forms on the page -->
    <div class="mt-3">
      <label for="invite_mobile_email" class="font-medium">Email</label>
      <input v-model="invite.email" v-validate="'required|email'" type="email" name="invite_email" data-vv-as="Email"
        :class="{ 'border-red': errors.has('invite_email') }" class="h-10 border rounded px-3 w-full mt-1"
        id="invite_mobile_email">
      <p class="text-xs text-red mt-1" v-if="errors.has('invite_email')">{{ errors.first('invite_email') }}</p>
    </div>

    <div class="mt-3">
      <label for="invite_mobile_first_name" class="font-medium">First name</label>
      <input v-model="invite.first_name" v-validate="'required'" type="text" name="invite_first_name" data-vv-as="First name"
        :class="{ 'border-red': errors.has('invite_first_name') }" class="h-10 border rounded px-3 w-full mt-1"
        id="invite_mobile_first_name">
      <p class="text-xs text-red mt-1" v-if="errors.has('invite_first_name')">{{ errors.first('invite_first_name') }}</p>
    </div>

    <div class="mt-3">
      <label for="invite_mobile_last_name" class="font-medium">Last name</label>
      <input v-model="invite.last_name" v-validate="'required'" type="text" name="invite_last_name" data-vv-as="Last name"
        :class="{ 'border-red': errors.has('invite_last_name') }" class="h-10 border rounded px-3 w-full mt-1"
        id="invite_mobile_last_name">
      <p class="text-xs text-red mt-1" v-if="errors.has('invite_last_name')">{{ errors.first('invite_last_name') }}</p>
    </div>

    <div class="mt-3">
      <label for="invite_mobile_number" class="font-medium">Mobile</label>
      <input v-model="invite.mobile_number" v-validate="'required'" type="text" name="invite_mobile_number" data-vv-as="Mobile"
        :class="{ 'border-red': errors.has('invite_mobile_number') }" class="h-10 border rounded px-3 w-full mt-1"
        id="invite_mobile_number">
      <p class="text-xs text-red mt-1" v-if="errors.has('invite_mobile_number')">{{ errors.first('invite_mobile_number') }}</p>
    </div>

    <div class="mt-3">
      <label class="font-medium">Permission profile</label>
      <div class="permission_role w-full mt-1">
        <multiselect v-model="invite.role" :class="{ 'invalid': errors.has('invite_role') }" track-by="id"
          :label="vault_type == 'strata' ? 'title' : 'role'" placeholder="Select" :options="roles"
          :showLabels="false" name="invite_role" data-vv-as="Permission profile" v-validate="'required'" :allow-empty="false" @input="checkRole">
        </multiselect>
      </div>
      <p class="text-xs text-red mt-1" v-if="errors.has('invite_role')">{{ errors.first('invite_role') }}</p>

      <div class="w-full mt-2" v-if="set_expiry && isUserF">
        <div class="flex">
          <flat-pickr :config="flatPickrConfig" :events="flatPickrEvents"
            class="appearance-none border rounded-l w-full py-2 px-3 text-black"
            placeholder="Start date" v-model="invite.start_date" name="start_date" data-vv-as="Start date"
            v-validate="set_expiry && invite.start_date == '' ? 'required' : ''"
            :class="{ 'border-red': errors.has('start_date') }" ref="flatPickerStart"></flat-pickr>
          <button type="button" class="text-white py-2 px-3 rounded-r" :class="errors.has('start_date') ? 'bg-red' : 'bg-brand'" data-toggle aria-label="Pick start date">
            <i class="fa fa-calendar-alt"></i>
          </button>
        </div>
        <p class="text-xs text-red mt-1" v-if="errors.has('start_date')">{{ errors.first('start_date') }}</p>
      </div>
      <div class="w-full mt-2" v-if="set_expiry">
        <div class="flex">
          <flat-pickr :config="flatPickrConfig" :events="flatPickrEvents"
            class="appearance-none border rounded-l w-full py-2 px-3 text-black"
            placeholder="Expiry date" v-model="invite.expiry_days" name="expiry_days" data-vv-as="Expiry date"
            v-validate="set_expiry && invite.expiry_days == '' ? 'required' : ''"
            :class="{ 'border-red': errors.has('expiry_days') }" ref="flatPickerExpiry"></flat-pickr>
          <button type="button" class="text-white py-2 px-3 rounded-r" :class="errors.has('expiry_days') ? 'bg-red' : 'bg-brand'" data-toggle aria-label="Pick expiry date">
            <i class="fa fa-calendar-alt"></i>
          </button>
        </div>
        <p class="text-xs text-red mt-1" v-if="errors.has('expiry_days')">{{ errors.first('expiry_days') }}</p>
      </div>
    </div>

    <div class="mt-3 mb-4">
      <label for="invite_mobile_unit" class="font-medium">{{ unitLabel || "Unit" }}</label>
      <input v-model="invite.unit_number" type="text" class="h-10 border rounded px-3 w-full mt-1" id="invite_mobile_unit">
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import { roleNeedsExpiry } from "../../vault_roles.mjs";

const blankInvite = () => ({
  email: "",
  title: "",
  first_name: "",
  last_name: "",
  unit_number: "",
  invitation_message_template_id: 1,
  role_id: "",
  expiry_days: "",
  start_date: "",
  is_valid: false,
});

export default {
  props: ["roles", "count", "unit-label"],

  components: {
    flatPickr,
  },

  data() {
    return {
      set_expiry: false,
      invite: blankInvite(),
      flatPickrConfig: {
        wrap: true,
        allowInput: false,
        dateFormat: "d/m/Y h:i K",
        enableTime: true,
        monthSelectorType: "static",
      },
      flatPickrEvents: [
        "on-change", "on-close", "on-destroy", "on-key-down", "on-month-change",
        "on-open", "on-parse-config", "on-ready", "on-value-update",
      ],
    };
  },

  methods: {
    checkRole(input) {
      this.set_expiry = roleNeedsExpiry(input, this.$store.getters.getCurrentVault.id);
    },

    getInviteDetails() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.invite.is_valid = true;
          this.$emit("invite-details", this.invite);
        }
      });
    },

    resetForm() {
      this.invite = blankInvite();
      this.$validator.reset();
    }
  },

  computed: {
    ...mapGetters({
      current_vault: "getCurrentVault"
    }),
    vault_type() {
      return window.vault_type;
    },
    isUserF() {
      if (this.invite.role && this.invite.role.name) {
        const roleName = this.invite.role.name.toLowerCase();
        return roleName.includes('user f');
      }
      return false;
    }
  },

  watch: {
    invite: function (value) {
      this.$nextTick(function () {
        this.checkRole(this.invite.role);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
input {
  border-color: #d8d8d8;
}

.permission_role>>>.multiselect__tags {
  padding-bottom: 6px !important;
}
</style>
